import React, { useState } from 'react';
import { FlaskConical, Loader2, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { requestFloorplanRefinementPreview } from '@/services/floorplanService';
import type { BoxCoordinates, FloorplanRefinementPreview } from '@/types';
import type { EditType } from '@/types/floorplanClassification';

interface FloorplanRefineInspectorPanelProps {
  imageId: string;
  versionId: string;
  /** Raw storage path (not a public/signed URL) of the state the next edit applies to. */
  currentStatePath: string;
  sourceStoragePath: string;
  projectId: string;
  userId: string;
  /** Shared with the pane's canvas region selector — the same box a real send would use. */
  box: BoxCoordinates | null;
  /**
   * "Mit diesem Prompt generieren" — sends a real (free, uncredited) edit via
   * the normal chat send path. `userPrompt` is the tested instruction (stored
   * as the message's display text + re-classified so needsReference/mask
   * decisions stay consistent with what was analyzed); `finalPrompt` is
   * threaded through as `finalPromptOverride`, which skips prompt assembly
   * and goes to the image model exactly as shown here.
   */
  onGenerateWithPrompt: (userPrompt: string, finalPrompt: string) => void;
}

const CLASSIFIER_PROMPT_KEY = 'floorplan_refinement_classifier_system';

const EDIT_TYPE_LABELS: Record<EditType, string> = {
  local_object_edit: 'lokal · Objekt',
  local_structural_edit: 'lokal · strukturell',
  global_style_edit: 'global · Stil',
  rebuild_required: 'Neuaufbau nötig',
  unclear: 'unklar',
};

// Admin-only: dry-runs the v2 classifier + prompt-assembly (no image, no
// credits) via requestFloorplanRefinementPreview (previewOnly=true), shows the
// routing decision + assembled prompt, lets the admin tune the classifier
// system prompt and the per-case assembly template (saved back to
// prompt_templates), and can trigger a real (charged) generation with the
// exact edited final prompt via onGenerateWithPrompt → finalPromptOverride.
export const FloorplanRefineInspectorPanel: React.FC<FloorplanRefineInspectorPanelProps> = ({
  imageId,
  versionId,
  currentStatePath,
  sourceStoragePath,
  projectId,
  userId,
  box,
  onGenerateWithPrompt,
}) => {
  const [userPrompt, setUserPrompt] = useState('');
  const [preview, setPreview] = useState<FloorplanRefinementPreview | null>(null);
  const [editedFinalPrompt, setEditedFinalPrompt] = useState('');

  // Inline prompt tuning (admin). Seeded from the first analysis; edited
  // values are sent back as overrides on every re-analysis.
  const [stagePromptsSeeded, setStagePromptsSeeded] = useState(false);
  const [classifierPrompt, setClassifierPrompt] = useState('');
  const [assemblyTemplate, setAssemblyTemplate] = useState('');
  // Which per-case assembly template is currently loaded (templateKey from the preview).
  const [assemblyKey, setAssemblyKey] = useState<string | null>(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<{ key: string; ok: boolean; text: string } | null>(null);

  const handleAnalyze = async () => {
    if (!userPrompt.trim() || !currentStatePath) return;
    setAnalyzing(true);
    setError(null);
    try {
      const p = await requestFloorplanRefinementPreview({
        imageId,
        versionId,
        // Dry-run only — the edge function's previewOnly branch returns before
        // any floorplan_messages write reaches this id (best-effort failure
        // logging elsewhere is a no-op update against a non-existent row).
        messageId: crypto.randomUUID(),
        userPrompt: userPrompt.trim(),
        boxCoordinates: box ?? undefined,
        currentStatePath,
        sourceStoragePath,
        projectId,
        userId,
        ...(stagePromptsSeeded
          ? {
              classifierPromptOverride: classifierPrompt,
              templateOverride: assemblyTemplate,
              templateOverrideKey: assemblyKey ?? undefined,
            }
          : {}),
      });
      setPreview(p);
      setEditedFinalPrompt(p.finalPrompt);
      if (!stagePromptsSeeded) {
        setClassifierPrompt(p.classifierPrompt);
        setAssemblyTemplate(p.template);
        setAssemblyKey(p.templateKey);
        setStagePromptsSeeded(true);
      } else if (p.templateKey !== assemblyKey) {
        // Classification flipped to a different case — the per-case assembly
        // editor re-seeds from the echoed template; the classifier prompt
        // (single, case-independent) stays as the admin edited it.
        setAssemblyTemplate(p.template);
        setAssemblyKey(p.templateKey);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async (key: string, content: string) => {
    setSavingKey(key);
    setSaveMsg(null);
    try {
      const { error: saveErr } = await supabase.from('prompt_templates').update({ content }).eq('key', key);
      if (saveErr) throw saveErr;
      setSaveMsg({ key, ok: true, text: 'Gespeichert' });
    } catch (e) {
      setSaveMsg({ key, ok: false, text: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' });
    } finally {
      setSavingKey(null);
    }
  };

  const canGenerate = editedFinalPrompt.trim().length > 0;

  return (
    <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 text-sm p-4 space-y-3">
      <div className="flex items-center gap-2 font-medium text-foreground">
        <FlaskConical className="w-4 h-4 text-primary" />
        Prompt-Analyse (Admin)
      </div>

      <div className="space-y-1">
        <label className="block text-[11px] font-medium text-slate-600">
          Testanweisung (nur Analyse — wird nicht gesendet)
        </label>
        <textarea
          value={userPrompt}
          onChange={(e) => { setUserPrompt(e.target.value); setPreview(null); }}
          rows={3}
          className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
          placeholder="z.B. 'Verschiebe die Wand zwischen Küche und Wohnzimmer nach rechts'"
        />
      </div>

      <Button size="sm" onClick={handleAnalyze} disabled={analyzing || !userPrompt.trim()}>
        {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
        Analysieren (kein Bild, keine Credits)
      </Button>

      {error && <p className="text-red-600 text-[12px]">⚠ {error} — kein Credit verbraucht.</p>}

      {preview && (
        <div className="space-y-2 border-t border-primary/20 pt-3">
          <div className="flex flex-wrap gap-1.5">
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary">
              {preview.edit_type ? (EDIT_TYPE_LABELS[preview.edit_type] ?? preview.edit_type) : 'keine Klassifikation'}
            </span>
            {preview.classification?.scope && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-200 text-slate-700">
                {preview.classification.scope === 'local' ? 'lokal' : 'global'}
              </span>
            )}
            {typeof preview.classification?.confidence === 'number' && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                Konfidenz {Math.round(preview.classification.confidence * 100)}%
              </span>
            )}
            {preview.needsReference && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                braucht Referenz-Grundriss
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
              <code className="text-slate-600">{preview.model}</code>
            </span>
          </div>

          {preview.region_description && (
            <p className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-2 py-1.5">
              <span className="font-semibold text-slate-600">Ort (KI):</span> {preview.region_description}
            </p>
          )}
          {preview.operation_instruction && (
            <p className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-2 py-1.5">
              <span className="font-semibold text-slate-600">Anweisung (KI):</span> {preview.operation_instruction}
            </p>
          )}
          {!preview.classification && (
            <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
              ⚠️ Keine Klassifikation (Classifier übersprungen/fehlgeschlagen) — der Finale Prompt basiert nur auf einer statischen Vorlage.
            </p>
          )}

          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[11px] font-medium text-slate-600">Stufe 1 · Classifier-System-Prompt</label>
              <Button
                size="sm"
                variant="outline"
                disabled={savingKey === CLASSIFIER_PROMPT_KEY}
                onClick={() => handleSave(CLASSIFIER_PROMPT_KEY, classifierPrompt)}
              >
                💾 Speichern
              </Button>
            </div>
            <textarea
              value={classifierPrompt}
              onChange={(e) => { setClassifierPrompt(e.target.value); if (saveMsg?.key === CLASSIFIER_PROMPT_KEY) setSaveMsg(null); }}
              rows={4}
              className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
            />
            {saveMsg?.key === CLASSIFIER_PROMPT_KEY && (
              <span className={saveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>{saveMsg.text}</span>
            )}
          </div>

          {preview.templateKey ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] font-medium text-slate-600">
                  Stufe 2 · Assembly-Template ({preview.templateKey})
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={savingKey === preview.templateKey}
                  onClick={() => handleSave(preview.templateKey as string, assemblyTemplate)}
                >
                  💾 Speichern
                </Button>
              </div>
              <textarea
                value={assemblyTemplate}
                onChange={(e) => { setAssemblyTemplate(e.target.value); if (saveMsg?.key === preview.templateKey) setSaveMsg(null); }}
                rows={6}
                className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
              />
              {saveMsg?.key === preview.templateKey && (
                <span className={saveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>{saveMsg.text}</span>
              )}
            </div>
          ) : (
            <p className="text-[11px] text-slate-400">Kein Assembly-Template zugeordnet (keine Klassifikation).</p>
          )}

          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-slate-600">
              Finaler Prompt (editierbar — geht exakt so ans Bild-Modell):
            </label>
            <textarea
              value={editedFinalPrompt}
              onChange={(e) => setEditedFinalPrompt(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
            />
            <p className="text-[11px] text-slate-400">
              „Mit diesem Prompt generieren" überspringt Classifier + Assembly und nutzt exakt diesen Text (Nachbessern ist kostenlos).
            </p>
          </div>

          <Button
            size="sm"
            disabled={!canGenerate}
            onClick={() => onGenerateWithPrompt(userPrompt.trim(), editedFinalPrompt)}
          >
            Mit diesem Prompt generieren
          </Button>
        </div>
      )}
    </div>
  );
};

export default FloorplanRefineInspectorPanel;
